import { Controller } from '@hotwired/stimulus'

// Fee Calculator Controller
//
// Dual entry:
//   Modal path  — open → input form → loader → results
//   Inline path — inline form under comparison tables → loader → results (modal input skipped)
//
// All calculations are client-side. No server requests.
export default class extends Controller {
  static outlets = ['auto-tabs']
  static targets = [
    'modal', 'inputView', 'loaderView', 'resultsView',
    'bookings', 'ticketCost', 'ticketsPerBooking',
    'revenueDisplay', 'calculateBtn',
    'inlineBookings', 'inlineTicketCost', 'inlineTicketsPerBooking',
    'inlineRevenueDisplay', 'inlineCalculateBtn',
    'rgCost', 'fhCost', 'pkCost', 'xlCost',
    'fhSavings', 'pkSavings', 'xlSavings',
    'revenueSubtitle',
    'maxSavingsBadge', 'maxSavingsText',
    'fhSavingsIcon', 'pkSavingsIcon', 'xlSavingsIcon',
    'fhSavingsBadge', 'pkSavingsBadge', 'xlSavingsBadge',
    // Inline results panel ("Your Savings" tab)
    'inlineLoaderView', 'inlineResultsView',
    'inlineRgCost', 'inlineFhCost', 'inlinePkCost', 'inlineXlCost',
    'inlineFhSavings', 'inlinePkSavings', 'inlineXlSavings',
    'inlineRevenueSubtitle',
    'inlineMaxSavingsBadge', 'inlineMaxSavingsText',
    'inlineFhSavingsIcon', 'inlinePkSavingsIcon', 'inlineXlSavingsIcon',
    'inlineFhSavingsBadge', 'inlinePkSavingsBadge', 'inlineXlSavingsBadge'
  ]

  connect() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this._boundKeyDown = this._handleKeyDown.bind(this)
  }

  disconnect() {
    document.removeEventListener('keydown', this._boundKeyDown)
  }

  // ── Public actions ─────────────────────────────────────────────────

  open(event) {
    this._triggerElement = event?.currentTarget
    this._openModal()
    this._showView('input')
    requestAnimationFrame(() => this.bookingsTarget.focus())
    this._trackEvent('fee_calculator_modal_opened')
  }

  close() {
    this.modalTarget.classList.remove('is-open')
    document.removeEventListener('keydown', this._boundKeyDown)

    const delay = this.reducedMotion ? 0 : 300
    setTimeout(() => this._showView('input'), delay)

    if (this._triggerElement) {
      this._triggerElement.focus()
      this._triggerElement = null
    }
    this._trackEvent('fee_calculator_modal_closed')
  }

  backdropClose(event) {
    if (event.target === event.currentTarget) this.close()
  }

  onModalInput() {
    const b = this._parse(this.bookingsTarget.value)
    const c = this._parse(this.ticketCostTarget.value)
    const t = this._parse(this.ticketsPerBookingTarget.value)
    this._updateFormState(b, c, t, this.revenueDisplayTarget, this.calculateBtnTarget)
  }

  onInlineInput() {
    const b = this._parse(this.inlineBookingsTarget.value)
    const c = this._parse(this.inlineTicketCostTarget.value)
    const t = this._parse(this.inlineTicketsPerBookingTarget.value)
    this._updateFormState(b, c, t, this.inlineRevenueDisplayTarget, this.inlineCalculateBtnTarget)
  }

  calculate(event) {
    event.preventDefault()

    const b = this._parse(this.bookingsTarget.value)
    const c = this._parse(this.ticketCostTarget.value)
    const t = this._parse(this.ticketsPerBookingTarget.value)

    this._runCalculation(b, c, t)
  }

  // Inline path — instead of opening the modal, reveal the "Your Savings" tab,
  // animate to it, show the loader, then grow into the results in place.
  calculateInline(event) {
    event.preventDefault()

    const b = this._parse(this.inlineBookingsTarget.value)
    const c = this._parse(this.inlineTicketCostTarget.value)
    const t = this._parse(this.inlineTicketsPerBookingTarget.value)

    // Defensive fallback to the modal flow if the tabs outlet isn't present.
    if (!this.hasAutoTabsOutlet) {
      this._syncToModal(b, c, t)
      this._openModal()
      this._runCalculation(b, c, t)
      return
    }

    this.autoTabsOutlet.showSavings()
    this._showInlineView('loader')

    const results = this._computeResults(b, c, t)
    const maxSavings = Math.max(results.fhSavings, results.pkSavings, results.xlSavings)
    this._trackEvent('fee_calculator_calculated', {
      path: 'inline',
      bookings: b,
      ticket_cost: c,
      tickets_per_booking: t,
      revenue: results.revenue,
      rg_cost: results.rg,
      fh_cost: results.fh,
      pk_cost: results.pk,
      xl_cost: results.xl,
      max_savings: maxSavings
    })

    const delay = this.reducedMotion ? 100 : 1200
    setTimeout(() => {
      this._renderInlineResults(results)
      this._showInlineView('results')
      this.autoTabsOutlet.setPanelsHeight(true)
    }, delay)
  }

  recalculate() {
    this._showView('input')
    requestAnimationFrame(() => this.bookingsTarget.focus())
    this._trackEvent('fee_calculator_recalculated', { path: 'modal' })
  }

  // Recalculate from the inline results — scroll back up to the inline form.
  recalculateInline() {
    const form = document.getElementById('fee-calc-form')
    form?.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'center' })
    requestAnimationFrame(() => this.inlineBookingsTarget.focus({ preventScroll: true }))
    this._trackEvent('fee_calculator_recalculated', { path: 'inline' })
  }

  trackDemoClick(event) {
    const path = event.params.path || 'unknown'
    this._trackEvent('fee_calculator_demo_clicked', { path })
  }

  // ── Private helpers ────────────────────────────────────────────────

  _openModal() {
    if (!this.modalTarget.classList.contains('is-open')) {
      this.modalTarget.classList.add('is-open')
      document.addEventListener('keydown', this._boundKeyDown)
    }
  }

  _syncToModal(b, c, t) {
    this.bookingsTarget.value = b > 0 ? b : ''
    this.ticketCostTarget.value = c > 0 ? c : ''
    this.ticketsPerBookingTarget.value = t > 0 ? t : ''
    this._updateFormState(b, c, t, this.revenueDisplayTarget, this.calculateBtnTarget)
  }

  _updateFormState(b, c, t, revenueEl, btnEl) {
    const revenue = this._roundMoney(b * c * t)
    revenueEl.textContent = this._fmt(revenue)
    btnEl.disabled = !(b > 0 && c > 0 && t > 0)
  }

  _runCalculation(b, c, t) {
    this._showView('loader')

    const results = this._computeResults(b, c, t)
    const maxSavings = Math.max(results.fhSavings, results.pkSavings, results.xlSavings)
    this._trackEvent('fee_calculator_calculated', {
      path: 'modal',
      bookings: b,
      ticket_cost: c,
      tickets_per_booking: t,
      revenue: results.revenue,
      rg_cost: results.rg,
      fh_cost: results.fh,
      pk_cost: results.pk,
      xl_cost: results.xl,
      max_savings: maxSavings
    })

    const delay = this.reducedMotion ? 100 : 1200
    setTimeout(() => {
      this._renderModalResults(results)
      this._showView('results')
    }, delay)
  }

  // Single source of truth for the fee math, shared by the modal and inline paths.
  _computeResults(b, c, t) {
    const revenue = this._roundMoney(b * c * t)
    const rg = this._roundMoney(b * 3)
    const fh = this._roundMoney(revenue * 0.06)
    const pk = this._roundMoney(revenue * 0.06)
    const xl = this._roundMoney(revenue * 0.06)

    return {
      revenue, rg, fh, pk, xl,
      fhSavings: fh - rg,
      pkSavings: pk - rg,
      xlSavings: xl - rg
    }
  }

  _renderModalResults(d) {
    this.rgCostTarget.textContent = this._fmt(d.rg)
    this.fhCostTarget.textContent = this._fmt(d.fh)
    this.pkCostTarget.textContent = this._fmt(d.pk)
    this.xlCostTarget.textContent = this._fmt(d.xl)
    this.fhSavingsTarget.textContent = this._fmt(d.fhSavings)
    this.pkSavingsTarget.textContent = this._fmt(d.pkSavings)
    this.xlSavingsTarget.textContent = this._fmt(d.xlSavings)
    this.revenueSubtitleTarget.textContent = this._fmt(d.revenue)

    const maxSavings = Math.max(d.fhSavings, d.pkSavings, d.xlSavings)
    if (maxSavings > 0) {
      this.maxSavingsTextTarget.textContent = this._fmt(maxSavings)
      this.maxSavingsBadgeTarget.style.display = 'inline-flex'
    } else {
      this.maxSavingsBadgeTarget.style.display = 'none'
    }

    this._updateBadge(this.fhSavingsBadgeTarget, this.fhSavingsIconTarget, d.fhSavings)
    this._updateBadge(this.pkSavingsBadgeTarget, this.pkSavingsIconTarget, d.pkSavings)
    this._updateBadge(this.xlSavingsBadgeTarget, this.xlSavingsIconTarget, d.xlSavings)
  }

  _renderInlineResults(d) {
    this.inlineRgCostTarget.textContent = this._fmt(d.rg)
    this.inlineFhCostTarget.textContent = this._fmt(d.fh)
    this.inlinePkCostTarget.textContent = this._fmt(d.pk)
    this.inlineXlCostTarget.textContent = this._fmt(d.xl)
    this.inlineFhSavingsTarget.textContent = this._fmt(d.fhSavings)
    this.inlinePkSavingsTarget.textContent = this._fmt(d.pkSavings)
    this.inlineXlSavingsTarget.textContent = this._fmt(d.xlSavings)
    this.inlineRevenueSubtitleTarget.textContent = this._fmt(d.revenue)

    const maxSavings = Math.max(d.fhSavings, d.pkSavings, d.xlSavings)
    if (maxSavings > 0) {
      this.inlineMaxSavingsTextTarget.textContent = this._fmt(maxSavings)
      this.inlineMaxSavingsBadgeTarget.style.display = 'inline-flex'
    } else {
      this.inlineMaxSavingsBadgeTarget.style.display = 'none'
    }

    this._updateBadge(this.inlineFhSavingsBadgeTarget, this.inlineFhSavingsIconTarget, d.fhSavings)
    this._updateBadge(this.inlinePkSavingsBadgeTarget, this.inlinePkSavingsIconTarget, d.pkSavings)
    this._updateBadge(this.inlineXlSavingsBadgeTarget, this.inlineXlSavingsIconTarget, d.xlSavings)
  }

  _showInlineView(name) {
    this.inlineLoaderViewTarget.classList.toggle('is-active', name === 'loader')
    this.inlineResultsViewTarget.classList.toggle('is-active', name === 'results')
  }

  _showView(name) {
    const views = {
      input:   this.inputViewTarget,
      loader:  this.loaderViewTarget,
      results: this.resultsViewTarget
    }
    for (const [key, el] of Object.entries(views)) {
      el.classList.toggle('is-active', key === name)
    }
  }

  _updateBadge(badgeEl, iconEl, value) {
    const negativeClass = 'fee-calc-modal__savings-badge--negative'

    if (value < 0) {
      badgeEl.classList.add(negativeClass)
      iconEl.classList.remove('bi-arrow-up')
      iconEl.classList.add('bi-arrow-down')
    } else {
      badgeEl.classList.remove(negativeClass)
      iconEl.classList.remove('bi-arrow-down')
      iconEl.classList.add('bi-arrow-up')
    }
  }

  _handleKeyDown(event) {
    if (event.key === 'Escape') this.close()
  }

  _parse(str) {
    const num = parseFloat(str)
    return Number.isFinite(num) ? num : 0
  }

  _roundMoney(num) {
    return Math.round(num * 100) / 100
  }

  _fmt(num) {
    const rounded = this._roundMoney(num)
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
      maximumFractionDigits: 2
    }).format(rounded)
  }

  _trackEvent(eventName, eventParams = {}) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, eventParams)
    } else {
      console.log('[GA Debug] event:', eventName, eventParams)
    }
  }
}
